import { ErrorCode, PortalApiError } from '@afterpaytouch/portal-api/types'
import { Route } from '../../config/router'
import { TrackingEvent } from '../../model/monitoring'
import { ErrorHandler, CheckoutErrorContext } from './types'
import { checkoutService } from '@afterpaytouch/portal-api/consumer/checkout'
import { authService } from '@afterpaytouch/portal-api/consumer/auth'
import { setEligibilityRemediationAttempted, setCheckoutError, setCheckoutStatusToBeLoaded, setForceDesktopGrantFlowUrl } from '../../state'
import { checkoutErrors, TerminalErrorCode } from '../../model'
import { isCashAppInAppBrowser, isInIframe } from '../device'
import { parseCashCohortConvergenceRemediation } from './cashCohortConvergenceRemediation'

const runForceNewAndRouteToCashAuth = async (context: CheckoutErrorContext, errorCode: ErrorCode, forceDesktopGrantFlowUrl: boolean): Promise<Route> => {
  context.logEvent(TrackingEvent.CHECKOUT_DECLINED, { isRecoverableTerminalError: true, errorCode })

  context.dispatch(setEligibilityRemediationAttempted(true))
  context.dispatch(setForceDesktopGrantFlowUrl(forceDesktopGrantFlowUrl))

  context.dispatch(checkoutService.util.resetApiState())
  context.dispatch(authService.util.resetApiState())
  context.dispatch(setCheckoutStatusToBeLoaded())

  const action = context.dispatch(authService.endpoints.cash.initiate({ ...context.cashAuthArgs, forceNewRequest: true }))
  try {
    await action.unwrap()
  } catch (err: unknown) {
    const rtkErr = err as { status?: unknown; data?: { message?: string }; message?: string }
    context.logEvent(TrackingEvent.CASH_FORCE_NEW_REQUEST_FAILED, {
      errorStatus: rtkErr?.status,
      errorMessage: rtkErr?.data?.message ?? rtkErr?.message,
    })
  } finally {
    action.unsubscribe()
  }

  return context.cashAuthRoute
}

const goTerminal = (context: CheckoutErrorContext, errorCode: ErrorCode, route: Route): Route => {
  context.logEvent(TrackingEvent.CHECKOUT_DECLINED, { isRecoverableTerminalError: false, errorCode })
  context.dispatch(setForceDesktopGrantFlowUrl(false))
  context.dispatch(
    setCheckoutError({
      error: checkoutErrors[errorCode as ErrorCode] ?? TerminalErrorCode.Default,
      errorCode,
    })
  )
  return route
}

const goRemediation = (context: CheckoutErrorContext, errorCode: ErrorCode, violations: string[]): Route => {
  context.logEvent(TrackingEvent.CHECKOUT_DECLINED, { isRecoverableTerminalError: true, errorCode })
  context.dispatch(setForceDesktopGrantFlowUrl(false))
  context.dispatch(
    setCheckoutError({
      error: checkoutErrors[errorCode as ErrorCode] ?? TerminalErrorCode.Default,
      errorCode,
      extra: {
        cashCohortConvergenceViolations: violations,
      },
    })
  )
  return Route.CASH_APP_PAY_REMEDIATION
}

export const createCheckoutStartConvergenceIneligibleErrorHandler = (): ErrorHandler => ({
  canHandle: (errorCode: ErrorCode): boolean => {
    return errorCode === ErrorCode.CashCohortConvergenceIneligible
  },

  handle: async (errorCode: ErrorCode, error: PortalApiError, context: CheckoutErrorContext): Promise<Route> => {
    const remediation = parseCashCohortConvergenceRemediation(error)

    if (remediation.status === 'terminal') {
      return goTerminal(context, errorCode, Route.CASH_APP_PAY_UNAVAILABLE)
    }

    const surfaceCanRunGrantFlow = !isCashAppInAppBrowser() && !isInIframe()

    if (surfaceCanRunGrantFlow && !context.eligibilityRemediationAttempted) {
      return runForceNewAndRouteToCashAuth(context, errorCode, true)
    }

    return goRemediation(context, errorCode, remediation.violations)
  },
})
